import React from 'react';
import { Icon, Card, StatusBadge } from '../../common';
import { getStatusMessage } from '../../../utils';
import { StorageService } from '../../../services';
import type { ConnectionStatus, FlowState } from '../../../types/whatsapp';

export interface ConnectionStatusProps {
  flowState: FlowState;
  connectionStatus: ConnectionStatus;
  onDisconnect: () => void;
}

export const ConnectionStatusCard: React.FC<ConnectionStatusProps> = ({
  flowState,
  connectionStatus,
  onDisconnect
}) => {
  // Get first phone number from storage if available
  const storageData = StorageService.getStorageData();
  const whatsappData = storageData?.whatsapp_business_onboarding;
  const phoneNumbers = whatsappData?.phone_numbers;
  const firstPhone = phoneNumbers && phoneNumbers.length > 0 ? phoneNumbers[0] : null;
  
  // Use business_id as userId if available
  const displayUserId = whatsappData?.business_id || connectionStatus.userId;
  return (
    <Card>
      <div className="mb-4">
        <div className="flex items-center mb-4">
          <h2 className="text-xl font-normal text-[#424242]">Estado de integración con Meta</h2>
          <div className="ml-5">
            <StatusBadge 
              status={flowState.currentStep} 
              message={getStatusMessage(flowState.currentStep)}
            />
          </div>
        </div>
      </div>

      <div className="mb-4">
        {!connectionStatus.isConnected && (
          <>
            <p className="text-gray-700 mb-2 text-sm font-normal">
              Antes de continuar, asegurate de tener una línea de WhatsApp Business activa y vinculada a tu cuenta de empresa en Meta.
            </p>
            
            <div className="mt-5 mb-2.5">
              <div className="bg-white border border-[#0288D1] rounded-lg p-4 flex items-start gap-3">
                <Icon name="link" className="w-5 h-5 text-[#0288D1] mt-0.5 flex-shrink-0" />
                <span className="text-[#01579B] text-sm">
                  Importante: Al iniciar la conexión con Meta, seleccioná "Conectar cuenta existente de WhatsApp Business". Si elegís la otra opción, se creará una nueva línea de WhatsApp API
                </span>
              </div>
            </div>
          </>
        )}
        
        <div className="flex gap-3 justify-end">
          {flowState.error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-700 text-sm">
                <strong>Error:</strong> {flowState.error}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Connection Details */}
      {connectionStatus.isConnected && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 relative">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-green-800 flex items-center gap-2">
              <Icon name="check" className="w-5 h-5" />
              Conexión Establecida
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Icon name="phone" className="w-4 h-4 text-green-600" />
              <span className="font-medium">Phone Number:</span>
              <span className="font-mono text-green-700">
                {firstPhone ? `${firstPhone.display_phone_number} (${firstPhone.id})` : connectionStatus.phoneNumberId || 'No configurado'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="building" className="w-4 h-4 text-green-600" />
              <span className="font-medium">WABA ID:</span>
              <span className="font-mono text-green-700">{connectionStatus.wabaId}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="clock" className="w-4 h-4 text-green-600" />
              <span className="font-medium">Conectado:</span>
              <span className="text-green-700">{connectionStatus.connectedAt?.toLocaleString('es-ES')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="user" className="w-4 h-4 text-green-600" />
              <span className="font-medium">Business ID:</span>
              <span className="font-mono text-green-700">{displayUserId || 'No configurado'}</span>
            </div>
          </div>
          <div className="flex justify-end mt-2">
            <button
              onClick={onDisconnect}
              className="text-red-600 hover:text-red-800 font-bold transition-colors duration-200 flex items-center gap-1"
              title="Desconectar WhatsApp Business"
            >
              Desconectar
            </button>
          </div>
        </div>
      )}
    </Card>
  );
};